import { DetailGroup } from 'components/UI/Responsive/DetailList';
import { isNil } from 'lodash';
import { Order } from 'model/Order';
import { dateUtils } from 'utils/date';
import { formatFullDecimal } from 'utils/number';
import { roundDecimals } from 'utils/util';
import { statusMap } from '../MyOrdersTable/maps/status';

const emptyValue = '-';

const textOrDash = (value?: string | number | null): string =>
  isNil(value) || value === '' ? emptyValue : String(value);

// Same rules as components/UI/Data/Monetary (symbol) and ShippingFee.
export const formatMoney = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNil(value) || numericValue === 0 || Number.isNaN(numericValue)) {
    return emptyValue;
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numericValue);
};

const formatShippingFee = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (numericValue === 0 || Number.isNaN(numericValue)) return emptyValue;
  return `R$${formatFullDecimal(numericValue)}`;
};

export const describeStatus = (order: Order): string =>
  statusMap[order.status]?.description ?? order.status;

export const describeSaleType = (saleType: string): string =>
  saleType === 'VOL' ? 'ENP' : saleType;

export const orderGrandTotal = (order: Order): string =>
  roundDecimals(order.total + order.shippingFee * order.unitQuantity, 2);

export const unitGrandTotal = (order: Order): string =>
  roundDecimals(order.unitPriceConsolidated + order.shippingFee, 6);

export const buildMyOrderDetailGroups = (order: Order): DetailGroup[] => [
  {
    name: 'Identificação',
    rows: [
      { label: 'Status', value: describeStatus(order) },
      ...(order.resultMessage
        ? [{ label: 'Mensagem', value: order.resultMessage }]
        : []),
      { label: 'Chave', value: textOrDash(order.accountCode) },
      { label: 'Pedido/Transação', value: textOrDash(order.orderNumber) },
      {
        label: 'GFP',
        value: order.gfpNumber === 0 ? emptyValue : textOrDash(order.gfpNumber),
      },
      { label: 'Tipo de compra', value: describeSaleType(order.saleType) },
      { label: 'Produtor', value: `${order.siteName} – ${order.siteCode}` },
    ],
  },
  {
    name: 'Datas',
    rows: [
      {
        label: 'Data de compra',
        value: dateUtils.formatDate(order.saleConclusion, 'DD/MM/YYYY HH:mm'),
      },
      {
        label: 'Data de entrega',
        value: order.isLKPSale
          ? emptyValue
          : dateUtils.formatDate(order.deliveryDate, 'DD/MM/YYYY'),
      },
      {
        label: 'Data de faturamento',
        value: dateUtils.formatDate(order.billingDate, 'DD/MM/YYYY'),
      },
    ],
  },
  {
    name: 'Produto',
    rows: [
      {
        label: 'Produto',
        value: order.conceptName
          ? `${order.description} ${order.conceptName.toUpperCase()}`
          : order.description,
      },
      { label: 'Código do produto', value: textOrDash(order.productCode) },
      { label: 'Código de barras', value: textOrDash(order.productBarCode) },
      { label: 'Qld', value: textOrDash(order.quality) },
      { label: 'Embalagem', value: textOrDash(order.packingName) },
      {
        label: 'QE x QpE',
        value: `${order.packingQuantity} x ${order.unitPerPacking}`,
      },
      { label: 'Total unidades', value: textOrDash(order.unitQuantity) },
    ],
  },
  {
    name: 'Valores',
    rows: [
      { label: 'Valor emb.', value: formatMoney(order.packingPrice) },
      {
        label: 'Valor unitário',
        value: formatMoney(roundDecimals(order.unitPrice, 6)),
      },
      { label: 'Valor total', value: formatMoney(order.subTotal) },
      {
        label: 'Frete unitário',
        value: formatShippingFee(roundDecimals(order.shippingFee ?? 0.0, 6)),
      },
      {
        label: 'Frete total',
        value: formatShippingFee(
          roundDecimals(order.shippingFee * order.unitQuantity, 6),
        ),
      },
      {
        label: 'Valor total unitário',
        value: formatMoney(unitGrandTotal(order)),
      },
      {
        label: 'Valor total geral',
        value: formatMoney(orderGrandTotal(order)),
      },
      {
        label: 'Adicional / desconto',
        value: textOrDash(order.voucherAmountLabel),
      },
    ],
  },
  {
    name: 'Outros',
    rows: [
      { label: 'Usuário', value: textOrDash(order.userCartInclusion) },
      {
        label: 'Conta',
        value: `${order.accountCode} - ${order.accountName}`,
      },
      { label: 'Observação', value: textOrDash(order.observation) },
    ],
  },
];
